"use client";

import React from "react";
import videojs from "video.js";
import Player from "video.js/dist/types/player";
import "video.js/dist/video-js.css";
import "videojs-youtube";
import "./index.css";

interface VideoPlayerProps {
  options: {
    sources: Array<{
      src: string;
      type: string;
    }>;
    techOrder?: string[];
    youtube?: {
      ytControls?: number;
      rel?: number;
      modestbranding?: number;
    };
    [key: string]: any;
  };
  onReady?: (player: Player) => void;
}

export const VideoPlayer = (props: VideoPlayerProps) => {
  const videoRef = React.useRef<HTMLDivElement>(null);
  const playerRef = React.useRef<Player | null>(null);
  const { options, onReady } = props;

  React.useEffect(() => {
    if (!playerRef.current && videoRef.current) {
      const videoElement = document.createElement("video-js");
      videoElement.classList.add("vjs-big-play-centered");
      videoRef.current.appendChild(videoElement);

      // Ensure YouTube tech is included in techOrder if needed
      const playerOptions = {
        ...options,
        techOrder:
          options.sources[0]?.type === "video/youtube"
            ? ["youtube"]
            : options.techOrder || ["html5"],
        youtube: {
          enablePrivacyEnhancedMode: true,
          ...options.youtube,
        },
      };

      const player = (playerRef.current = videojs(
        videoElement,
        playerOptions,
        () => {
          onReady?.(player);
        }
      ));

      // Add YouTube specific options if needed
      if (options.sources[0]?.type === "video/youtube") {
        player.ready(() => {
          player.src({
            type: "video/youtube",
            src: options.sources[0].src,
          });
        });
      }
    } else {
      const player = playerRef.current;
      if (player) {
        player.autoplay(options.autoplay);
        player.src(options.sources);
      }
    }
  }, [options, onReady]);

  // Dispose the Video.js player when the component unmounts
  React.useEffect(() => {
    const player = playerRef.current;
    return () => {
      if (player && !player.isDisposed()) {
        player.dispose();
        playerRef.current = null;
      }
    };
  }, []);

  return (
    <div data-vjs-player>
      <div ref={videoRef} />
    </div>
  );
};

export default VideoPlayer;
